<script setup>
import { onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import Header from '@/components/Header.vue'
import DesignFooter from '@/components/DesignFooter.vue'
import { setTrack } from '@/api/login'
import {
  capabilities,
  demoCards,
  iotStages,
  productPages,
} from './data'

const skillsHubUrl = 'https://aipg.quectel.com/skills'
const deviceTitles = ['本地记忆', '聊天对话即可定义设备行为', '场景化技能扩展']
const deviceCards = demoCards.map((item, index) => ({
  title: deviceTitles[index],
  desc: item.desc,
}))
const isExternal = (path) => /^https?:\/\//.test(path)

const track = async (position) => {
  try {
    await setTrack({ position })
  } catch (error) {
    console.warn('UniClaw track failed:', error)
  }
}

const getTrackPosition = (path) => {
  if (path === skillsHubUrl) return 'uniclaw > skills_hub'
  return `uniclaw > ${path}`
}

const trackLink = (path) => {
  if (!path.startsWith('/uniclaw/docs')) {
    void track(getTrackPosition(path))
  }
}

onMounted(() => {
  track('uniclaw')
})
</script>

<template>
  <Header design-always hide-banner />
  <main class="uc-page">
    <section class="uc-hero">
      <div class="uc-wrap">
        <div class="uc-hero-copy">
          <h1>把 Agent 大脑交给真实硬件</h1>
          <p class="uc-hero-desc">
            UniClaw｜面向 MCU/SoC 嵌入式硬件的端云一体 Agent
            开发框架，为IoT设备提供感知、决策、行动和本地记忆能力
          </p>
        </div>
        <RouterLink class="uc-button" to="/uniclaw/docs" @click="trackLink('/uniclaw/docs')">
          查看文档
        </RouterLink>
      </div>
    </section>

    <section class="uc-section">
      <div class="uc-wrap">
        <div class="uc-section-head">
          <div class="uc-title-block">
            <h2>快速开始</h2>
            <p class="uc-lead">
              完成
              UniClaw快速入门、资源下载与技能扩展，一站式打通Agent硬件开发全流程
            </p>
          </div>
        </div>
        <div class="uc-grid uc-three-grid">
          <article
            v-for="item in productPages"
            :key="item.title"
            class="uc-card uc-action-card"
          >
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
            <component
              :is="isExternal(item.href) ? 'a' : RouterLink"
              class="uc-link-row"
              v-bind="isExternal(item.href)
                ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
                : { to: item.href }"
              @click="trackLink(item.href)"
            >
              {{ item.cta }}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 12h16m-6-6 6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </component>
          </article>
        </div>
      </div>
    </section>

    <section class="uc-section uc-soft">
      <div class="uc-wrap">
        <div class="uc-section-head">
          <div class="uc-title-block">
            <h2>四步完成UniClaw设备部署</h2>
            <p class="uc-lead">
              从零搭建Agent智能硬件，覆盖开发板选择、环境部署、参数配置、对话验证的完整链路
            </p>
          </div>
        </div>
        <div class="uc-grid uc-stage-grid">
          <article
            v-for="stage in iotStages"
            :key="stage.title"
            class="uc-card"
          >
            <h3 class="uc-stage-title">
              <span class="uc-stage-no">{{ stage.no }}</span>
              {{ stage.title }}
            </h3>
            <p>{{ stage.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="uc-section">
      <div class="uc-wrap">
        <div class="uc-section-head">
          <div class="uc-title-block">
            <h2>核心能力</h2>
            <p class="uc-lead">一套框架覆盖端云协同全链路，赋能硬件自主智能</p>
          </div>
        </div>
        <div class="uc-grid uc-three-grid">
          <article
            v-for="item in capabilities"
            :key="item.title"
            class="uc-card"
          >
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="uc-section uc-soft">
      <div class="uc-wrap">
        <div class="uc-section-head">
          <div class="uc-title-block">
            <h2>UniClaw如何驱动真实设备</h2>
            <p class="uc-lead">重构IoT设备交互方式，加速AI迈入物理世界</p>
          </div>
        </div>
        <div class="uc-grid uc-three-grid">
          <article
            v-for="item in deviceCards"
            :key="item.title"
            class="uc-card"
          >
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

  </main>
  <DesignFooter />
</template>

<style scoped>
.uc-page {
  color: #14233b;
  background: #fff;
}

.uc-page,
.uc-page *,
.uc-page *::before,
.uc-page *::after {
  box-sizing: border-box;
}

.uc-page h1,
.uc-page h2,
.uc-page h3,
.uc-page p {
  margin: 0;
}

.uc-wrap {
  width: min(1440px, calc(100% - 48px));
  margin-inline: auto;
}

.uc-hero {
  display: flex;
  align-items: center;
  min-height: 416px;
  padding-block: 64px;
  color: #fff;
  background: linear-gradient(156.57deg, rgba(13, 24, 43, 0) 0%, #2b3066 92.593%), #0d182b;
}

.uc-hero-copy {
  display: flex;
  flex-direction: column;
  gap: 16px;
}


.uc-page h1 {
  color: inherit;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.2;
}

.uc-hero-desc {
  max-width: 820px;
  color: rgb(255 255 255 / 80%);
  font-size: 16px;
  line-height: 1.5;
}

.uc-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 48px;
  margin-top: 32px;
  border-radius: 4px;
  background: #ed000c;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;
}

.uc-button:hover {
  background: #c9000a;
  color: #fff;
}

.uc-section {
  padding-block: 64px;
}

.uc-soft {
  background: #f4f7fb;
}

.uc-section-head {
  margin-bottom: 32px;
  text-align: center;
}

.uc-page h2 {
  color: inherit;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
}

.uc-title-block {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.uc-lead,
.uc-card p {
  color: #617089;
  font-size: 16px;
  line-height: 24px;
}

.uc-grid {
  display: grid;
  grid-auto-rows: 1fr;
  gap: 24px;
  align-items: stretch;
}

.uc-three-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.uc-stage-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.uc-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  padding: 24px;
  border: 1px solid #dce2ea;
  border-radius: 16px;
  background: #fff;
  overflow-wrap: anywhere;
}

.uc-page h3 {
  color: inherit;
  font-size: 18px;
  font-weight: 600;
  line-height: 27px;
}

.uc-stage-title {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.uc-stage-no {
  flex-shrink: 0;
  color: #ed000c;
}

.uc-link-row {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 8px;
  margin-top: auto;
  padding-top: 8px;
  color: #ed000c;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  text-decoration: none;
}

.uc-link-row svg {
  flex-shrink: 0;
}

.uc-link-row:hover {
  color: #c9000a;
  text-decoration: underline;
}

.uc-page a:focus-visible {
  outline: 3px solid #1478d4;
  outline-offset: 4px;
}

@media (min-width: 768px) {
  .uc-page h1 { font-size: 36px; }
  .uc-page h2 { font-size: 26px; }
  .uc-hero-desc { font-size: 18px; }
}

@media (min-width: 992px) {
  .uc-page h1 { font-size: 40px; }
  .uc-page h2 { font-size: 28px; }
}

@media (min-width: 1200px) {
  .uc-page h1 { font-size: 44px; }
  .uc-page h2 { font-size: 30px; }
}

@media (min-width: 1400px) {
  .uc-page h2 { font-size: 32px; }
}

@media (max-width: 1199px) {
  .uc-stage-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 991px) {
  .uc-three-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 767px) {
  .uc-section { padding-block: 40px; }
  .uc-three-grid,
  .uc-stage-grid { grid-template-columns: minmax(0, 1fr); }
}
</style>
